import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { atom, useRecoilState, useSetRecoilState } from "recoil";
import { axios } from "../lib/request";
import { nubsQueryKey, nubsUri } from "../lib/refresh";
import { useGameQuery } from "./Settings.state";
import { createNub, useReorderAfterDelete } from "../common/NubsUpload/NubsUpload.state";
import { acceptedImages } from '../consts/accepted-files'
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";
import { getErrorMessage } from "../lib/error";

export const isResettingGameImageState = atom({
    key: 'isResettingGameImage',
    default: false,
})

export const fetchNubs = async (game_id) => {
    if (game_id === "new") {
        return { nubs: [], assets: { videos: [] } };
    }

    const response = await axios.get(nubsUri(game_id));
    const { data } = response

    // Nubs that are attached to a task_id will return null
    // when they are retrieved with only game_id as the parameter:
    // https://apidocs.socialscavenger.com/#retrieve-nubs
    const videos = []
    data.nubs = data.nubs.filter(n => n !== null)
    data.nubs.forEach(nub => {
        // Create assets object that matches the current task assets' structure
        // so that it can reuse the VideoThumbnail component and the useVideoThumbnail hook
        const isVideoNub = nub.media_uuid  && nub.media_extension.match(/(?:avi|mkv|mov|mp4|webm)$/g)
        const nubVideo = {
            name: nub.media_uuid,
            urls: {
                mp4: nub.media_url,
                png: `https://${process.env.REACT_APP_WHITELABEL}-assets.socialscavenger.com/images/${nub.media_uuid}-00001.png`
            }
        }
        if (isVideoNub) {
            videos.push(nubVideo)
        }
    })
    data.assets = { videos }
    return data;
};

export const useNubsDataQuery = (game_id) => {
    const queryKey = nubsQueryKey(game_id);
    const setAlert = useSetRecoilState(alertState);
    return useQuery(
        queryKey,
        () => fetchNubs(game_id),
        {
            suspense: true,
            useErrorBoundary: false,
            onError: (error) => {
                const message = getErrorMessage(error);
                setAlert({ type: "error", message, closable: true });
            },
        }
    );
};

export const useNubsItemQuery = (data, queryKey, config = {}) => {
    const { invalidate = true, invalidateKey, ...invalidationConfig } = config
    const queryClient = useQueryClient();

    const setItem = useCallback(
        async (params) => {
            queryClient.cancelQueries(queryKey);
            queryClient.setQueryData(queryKey, params);

            const shouldInvalidate = invalidate
            if (shouldInvalidate) {
                queryClient.invalidateQueries((invalidateKey || queryKey), { ...invalidationConfig });
            }
        },
        [queryClient, queryKey, invalidate, invalidateKey, invalidationConfig]
    );

    return [{
        nubs: (data?.nubs || []),
        assets: { videos: (data?.assets?.videos || []) }
    }, setItem];
};

export const useNubsQuery = (config = {}) => {
    const { game_id } = useParams();
    const { data, isFetching } = useNubsDataQuery(game_id);
    const queryKey = nubsQueryKey(game_id);
    return [...useNubsItemQuery(data, queryKey, config), isFetching];
};

export const useExistingGameImageQuery = () => {
    const { game_id } = useParams()
    const [{ assets }, setCollection] = useGameQuery(game_id)
    const existingPhoto = useMemo(() => {
        const gameImage = (assets?.photos || [])?.find(({ name }) => name === 'standard')?.url || ''
        const imageExtensionsReg = new RegExp(`(.*)+\\.(${acceptedImages.map(ext => ext.replace('.', '')).join('|')})$`)
        const gameImageUuid = gameImage.split('/').pop().match(imageExtensionsReg)
        const uuid = gameImageUuid?.[1]
        const extension = gameImageUuid?.[2]
        return {
            gameImage,
            gameImageUuid,
            uuid,
            extension,
        }
    }, [assets?.photos])
    const updateGame = async (imageNub = {}) => {
        await setCollection({
            assets: {
                ...assets,
                photos: [
                    ...assets.photos?.filter(p => p.name !== 'standard'),
                    {
                        name: 'standard',
                        url: imageNub.media_url,
                    }
                ]
            },
            photo_media_uuid: imageNub.media_uuid,
            photo_media_extension: imageNub.media_extension,
        })
    }
    return [existingPhoto, updateGame]
}

export const useDeleteNubMutation = ({ useQuery }) => {
    const { game_id } = useParams()
    const [collection, setCollection] = useQuery({ invalidate: false })
    const [existingImage, updateGame] = useExistingGameImageQuery()
    const [, setIsResettingGameImage] = useRecoilState(isResettingGameImageState)
    const reOrder = useReorderAfterDelete({ useQuery })
    const { mutateAsync } = useMutation(async (nub) => {
        if (existingImage?.uuid === nub.media_uuid) {
            setIsResettingGameImage(true)
            await updateGame({
                media_url: '',
                media_uuid: null,
                media_extension: null,
            })
        }
        await setCollection({
            ...collection,
            nubs: [...collection.nubs]?.filter(
                n => parseInt(n.id) !== parseInt(nub.id)
            ),
        })
        const url = `${process.env.REACT_APP_API_NUBS_UPDATE_URL}/${nub.id}.json?game_id=${game_id}`;
        await axios.delete(url);
        await reOrder(nub)
        setIsResettingGameImage(false)
    });
    return mutateAsync
}

export const useBackwardCompatibleNubs = () => {
    const { game_id } = useParams()
    const [collection, setCollection] = useNubsQuery()
    const [{
        gameImage,
        gameImageUuid,
        uuid,
        extension,
    }] = useExistingGameImageQuery()
    const [uploadingNub, setUploadingNub] = useState(false)
    useEffect(() => {
        /**
         * if there is an existing game image, and no nubs, 
         * create a first nub with the original game image as migration.
         */
        let synced = false
        if (game_id !== "new" && !synced && !uploadingNub && gameImageUuid && collection.nubs?.length === 0) {
            const uploadAsNub = async () => {
                setUploadingNub(true)
                try {
                    const newNub = {
                        position: collection.nubs?.length + 1,
                        [`media_uuid`]: uuid,
                        [`media_extension`]: extension.toLocaleLowerCase(),
                        [`media_url`]: gameImage,
                    }
                    const created = await createNub(game_id, {
                        ...newNub,
                    })
                    if (created?.data?.nub) {
                        await setCollection({
                            ...collection,
                            nubs: [
                                ...collection?.nubs,
                                {
                                    ...created.data.nub,
                                    ...newNub,
                                },
                            ],
                        });
                    }
                } finally {
                    setUploadingNub(false)
                }
            }
            uploadAsNub()
        }
        return () => {
            synced = true
        }
    }, [
        uploadingNub,
        collection,
        setCollection,
        gameImage,
        gameImageUuid,
        uuid,
        extension,
        game_id,
    ])
}

export const useSyncNubsWithGameImage = () => {
    const [collection] = useNubsQuery()
    const [{ assets }, updateGame] = useGameQuery()
    const [isResettingGameImage] = useRecoilState(isResettingGameImageState)
    const { game_id } = useParams()
    useEffect(() => {
        /**
         * if there is no existing game image, 
         * then the first image uploaded to carousel should also be set at the original game image 
         */
        let synced = false
        const gameImage = (assets?.photos || []).find(({ name }) => name === 'standard')?.url
        const imageExtensionsReg = new RegExp(acceptedImages.map(ext => ext.replace('.', '')).join('|'))
        const imageNub = (collection.nubs || [])?.find(n => n?.media_extension?.match(imageExtensionsReg))
        if (game_id !== "new" && !synced && !isResettingGameImage && imageNub && !gameImage) {
            let mediaUrl = imageNub.media_url
            // only use the media_url that has been resolved from the server
            // and not using local media URL (base64)
            if (imageNub.media_url?.match(/^data:image/)) {
                mediaUrl = imageNub.media_url_raw
            }
            updateGame({
                assets: {
                    ...assets,
                    photos: [
                        ...(assets?.photos || []).filter(p => p.name !== 'standard'),
                        {
                            name: 'standard',
                            url: mediaUrl,
                        }
                    ]
                },
                photo_media_uuid: imageNub.media_uuid,
                photo_media_extension: imageNub.media_extension,
            })
        }
        return () => {
            synced = true
        }
    }, [
        assets,
        collection.nubs,
        updateGame,
        isResettingGameImage,
        game_id,
    ])
}